import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

function activeIllustrationForUnit(
  assets,
  unitId
) {
  const identity =
    clean(unitId);

  if (!identity) {
    return null;
  }

  return (
    Array.isArray(assets)
      ? assets
      : []
  ).find(
    (asset) =>
      asset?.assetType ===
        "illustration" &&
      asset?.purpose ===
        "book_map_illustration" &&
      asset?.status ===
        "active" &&
      clean(
        asset?.bookMapUnitId
      ) === identity
  ) || null;
}

function protectedAsset(
  asset
) {
  return Boolean(
    asset &&
    (
      asset.source ===
        "upload" ||
      asset.storageProvider ===
        "local"
    )
  );
}

function safeImageUrl(
  value
) {
  const candidate =
    clean(value);

  if (!candidate) {
    return "";
  }

  try {
    const parsed =
      new URL(candidate);

    return (
      parsed.protocol ===
        "http:" ||
      parsed.protocol ===
        "https:"
    )
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
}

function illustrationPlanLabel(
  value,
  isFR
) {
  const key =
    clean(value)
      .toLowerCase();

  const labels = {
    small_illustration:
      isFR
        ? "Petite illustration"
        : "Small illustration",

    half_page:
      isFR
        ? "Illustration sur une demi-page"
        : "Half-page illustration",

    full_page:
      isFR
        ? "Illustration pleine page"
        : "Full-page illustration",

    full_spread:
      isFR
        ? "Illustration sur toute la double page"
        : "Full-spread illustration",

    integrated_text:
      isFR
        ? "Illustration intégrée au texte"
        : "Illustration integrated with text",
  };

  return (
    labels[key] ||
    (
      isFR
        ? "Illustration prévue"
        : "Illustration planned"
    )
  );
}

export default function ChildrensBookManuscriptIllustrationContext({
  lang = "en",
  unit,
  childrensBookAssets = [],
  onLoadChildrensBookAssetFile,
  presentation = "context",
}) {
  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const presentationMode =
    clean(
      presentation
    ).toLowerCase();

  const authoringSurfacePresentation =
    presentationMode ===
      "authoring_surface";

  const pagePresentation =
    (
      authoringSurfacePresentation ||
      presentationMode ===
        "page" ||
      presentationMode ===
        "shared_small" ||
      presentationMode ===
        "shared_half"
    );

  const sharedPagePresentation =
    (
      presentationMode ===
        "shared_small" ||
      presentationMode ===
        "shared_half"
    );

  const pagePresentationMinHeight =
    authoringSurfacePresentation
      ? undefined
      : (
          presentationMode ===
            "shared_small"
            ? 135
            : (
                presentationMode ===
                  "shared_half"
                  ? 270
                  : 470
              )
        );

  /*
   * Shared Small / Half-page previews represent a bounded
   * portion of one writing page.
   *
   * A minimum height alone is insufficient for a real image:
   * its intrinsic aspect ratio can enlarge the preview region.
   *
   * Full-page "page" presentation deliberately remains
   * flexible and is not constrained by this shared-page rule.
   */
  const sharedPagePresentationHeight =
    sharedPagePresentation
      ? pagePresentationMinHeight
      : undefined;

  const unitId =
    clean(unit?.id);

  const requirement =
    clean(
      unit?.illustrationPlan
    ).toLowerCase();

  const requiresIllustration =
    Boolean(
      requirement &&
      requirement !==
        "none"
    );

  const activeIllustration =
    useMemo(
      () =>
        activeIllustrationForUnit(
          childrensBookAssets,
          unitId
        ),
      [
        childrensBookAssets,
        unitId,
      ]
    );

  const [
    previewUrl,
    setPreviewUrl,
  ] = useState("");

  const [
    previewLoading,
    setPreviewLoading,
  ] = useState(false);

  const [
    previewError,
    setPreviewError,
  ] = useState("");

  const isProtected =
    protectedAsset(
      activeIllustration
    );

  /*
   * Keep protected-preview loading dependent on the
   * primitive asset facts it actually consumes rather
   * than the complete asset object identity.
   */
  const hasActiveIllustration =
    Boolean(
      activeIllustration
    );

  const activeIllustrationPublicUrl =
    clean(
      activeIllustration
        ?.publicUrl
    );

  useEffect(() => {
    let cancelled =
      false;

    let objectUrl =
      "";

    setPreviewUrl("");
    setPreviewError("");
    setPreviewLoading(false);

    if (
      !requiresIllustration ||
      !hasActiveIllustration
    ) {
      return () => {};
    }

    if (!isProtected) {
      setPreviewUrl(
        safeImageUrl(
          activeIllustrationPublicUrl
        )
      );

      return () => {};
    }

    const publicUrl =
      activeIllustrationPublicUrl;

    if (
      !publicUrl ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setPreviewError(
        isFR
          ? "L’aperçu de l’illustration enregistrée n’est pas disponible."
          : "The saved illustration preview is unavailable."
      );

      return () => {};
    }

    setPreviewLoading(true);

    async function loadPreview() {
      try {
        const blob =
          await onLoadChildrensBookAssetFile(
            publicUrl
          );

        if (cancelled) {
          return;
        }

        if (
          typeof URL ===
            "undefined" ||
          typeof URL
            .createObjectURL !==
            "function"
        ) {
          throw new Error(
            isFR
              ? "Le navigateur n’a pas pu préparer l’aperçu."
              : "The browser could not prepare the preview."
          );
        }

        objectUrl =
          URL.createObjectURL(
            blob
          );

        if (!cancelled) {
          setPreviewUrl(
            objectUrl
          );
        }
      } catch (error) {
        if (!cancelled) {
          setPreviewError(
            clean(
              error?.message
            ) ||
            (
              isFR
                ? "Impossible de charger l’illustration enregistrée."
                : "Could not load the saved illustration."
            )
          );
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(
            false
          );
        }
      }
    }

    loadPreview();

    return () => {
      cancelled = true;

      if (
        objectUrl &&
        typeof URL !==
          "undefined" &&
        typeof URL
          .revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [
    activeIllustrationPublicUrl,
    hasActiveIllustration,
    isFR,
    isProtected,
    onLoadChildrensBookAssetFile,
    requiresIllustration,
  ]);

  if (!requiresIllustration) {
    return null;
  }

  const planLabel =
    illustrationPlanLabel(
      requirement,
      isFR
    );

  const unitTitle =
    clean(
      unit?.workingTitle
    );

  return (
    <section
      data-testid="manuscript-illustration-context"
      aria-label={
        isFR
          ? "Contexte de l’illustration"
          : "Illustration context"
      }
      data-presentation={
        authoringSurfacePresentation
          ? "authoring_surface"
          : (
              pagePresentation
                ? "page"
                : "context"
            )
      }
      style={{
        marginTop:
          pagePresentation
            ? 0
            : 18,
        padding:
          pagePresentation
            ? 0
            : 16,
        borderRadius:
          pagePresentation
            ? 0
            : 14,
        border:
          pagePresentation
            ? "none"
            : "1px solid rgba(21,128,61,0.22)",
        background:
          pagePresentation
            ? "transparent"
            : "#f8fafc",
        minHeight:
          pagePresentation
            ? "100%"
            : undefined,
        display:
          pagePresentation
            ? "flex"
            : undefined,
        flexDirection:
          pagePresentation
            ? "column"
            : undefined,
      }}
    >
      {!pagePresentation ? (
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.06em",
              textTransform:
                "uppercase",
              color: "#64748b",
            }}
          >
            {isFR
              ? "Illustration"
              : "Illustration"}
          </div>

          <strong
            style={{
              display: "block",
              marginTop: 4,
              color: "#1f2937",
            }}
          >
            {planLabel}
          </strong>
        </div>

        {activeIllustration ? (
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: "#166534",
            }}
          >
            {isFR
              ? "Image jointe"
              : "Image attached"}
          </span>
        ) : (
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: "#9a3412",
            }}
          >
            {isFR
              ? "Image à ajouter"
              : "Image to be added"}
          </span>
        )}
      </div>      ) : null}


      {previewLoading ? (
        <div
          role="status"
          style={{
            marginTop: 14,
            color: "#475569",
            fontWeight: 700,
          }}
        >
          {isFR
            ? "Chargement de l’illustration…"
            : "Loading illustration…"}
        </div>
      ) : null}

      {previewError ? (
        <div
          role="alert"
          style={{
            marginTop: 14,
            color: "#b91c1c",
            fontWeight: 700,
          }}
        >
          {previewError}
        </div>
      ) : null}

      {previewUrl ? (
        <div
          style={{
            marginTop:
              pagePresentation
                ? 0
                : 14,
            display: "grid",
            placeItems: "center",
            padding:
              pagePresentation
                ? 0
                : 12,
            borderRadius:
              pagePresentation
                ? 8
                : 12,
            background:
              "#ffffff",
            border:
              pagePresentation
                ? "none"
                : "1px solid rgba(148,163,184,0.28)",
            flex:
              pagePresentation &&
              !sharedPagePresentation
                ? 1
                : undefined,
            height:
              sharedPagePresentation
                ? sharedPagePresentationHeight
                : undefined,
            minHeight:
              pagePresentation
                ? pagePresentationMinHeight
                : undefined,
            maxHeight:
              sharedPagePresentation
                ? sharedPagePresentationHeight
                : undefined,
            overflow:
              "hidden",
          }}
        >
          <img
            src={previewUrl}
            alt={
              isFR
                ? `Aperçu de l’illustration${unitTitle ? ` — ${unitTitle}` : ""}`
                : `Illustration preview${unitTitle ? ` — ${unitTitle}` : ""}`
            }
            style={{
              display: "block",
              width: "100%",
              height:
                sharedPagePresentation
                  ? sharedPagePresentationHeight
                  : (
                      pagePresentation
                        ? "100%"
                        : "auto"
                    ),
              maxWidth:
                sharedPagePresentation
                  ? "100%"
                  : (
                      pagePresentation
                        ? "none"
                        : 520
                    ),
              maxHeight:
                sharedPagePresentation
                  ? sharedPagePresentationHeight
                  : (
                      pagePresentation
                        ? "none"
                        : 320
                    ),
              objectFit:
                "contain",
              objectPosition:
                pagePresentation
                  ? "center center"
                  : undefined,
              borderRadius: 8,
            }}
          />
        </div>
      ) : !activeIllustration &&
        !previewLoading ? (
        <div
          style={{
            marginTop: 14,
            height:
              sharedPagePresentation
                ? sharedPagePresentationHeight
                : undefined,
            minHeight:
              pagePresentation
                ? pagePresentationMinHeight
                : 120,
            maxHeight:
              sharedPagePresentation
                ? sharedPagePresentationHeight
                : undefined,
            display: "grid",
            placeItems: "center",
            padding: 18,
            textAlign: "center",
            borderRadius: 12,
            border:
              "1px dashed rgba(154,52,18,0.38)",
            background:
              "#fffaf5",
            color: "#9a3412",
            fontWeight: 700,
          }}
        >
          {isFR
            ? "Une illustration est prévue ici, mais aucune image n’a encore été jointe."
            : "An illustration is planned here, but no image has been attached yet."}
        </div>
      ) : null}
    </section>
  );
}
